'use client';
import * as React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import dynamic from 'next/dynamic';
import SocialLinks from '@components/shared/SocialLinks';
import LanguageSelector from '@components/LanguageSelector';
import FooterColumn, { ColumnProps } from './_FooterColumn';
import MarketingCaptureForm from '@repo/ui/src/components/MarketingCapture/_MarketingCaptureForm';
import { classNames } from '@repo/ui/src/utils/classNames';
import { MarketingSignupProps } from '@typings/marketingSignup';
import { Anchor } from '@repo/ui/src/components/Anchor';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

export interface FooterProps {
  emailSignup?: MarketingSignupProps;
  smsSignup?: MarketingSignupProps;
  bottomRowTitle: string;
  submit: string;
  disclaimer?: string;
  columns: Array<ColumnProps>;
}

const Footer = ({
  columns,
  bottomRowTitle,
  emailSignup,
  smsSignup,
  disclaimer,
  submit = 'Sign Me Up',
}: FooterProps) => {
  const { t } = useTranslation();
  const doNotSellText = t('cookieConsentManager.doNotSellText', undefined, {
    default: 'none',
  });

  return (
    <footer className="tw-bg-neutral-900 tw-relative">
      <div className="tw-flex tw-justify-between tw-max-w-[1200px] tw-mx-auto tw-px-6 tw-py-12 tw-flex-col sm:tw-flex-row md:tw-px-0">
        <FooterColumn columns={columns} />
        <div>
          {(emailSignup || smsSignup) && (
            <div className="tw-pb-6 tw-grid tw-flex-col tw-grid-col tw-gap-5 tw-max-w-[370px]">
              <MarketingCaptureForm
                emailSignup={emailSignup}
                smsSignup={smsSignup}
                darkMode
                success={emailSignup?.success}
                failure={emailSignup?.failure}
                submitButton={submit}
                sourceDetail="footer"
                fullWidth
              />
              {disclaimer && (
                <Markdown
                  className={classNames(
                    'tw-text-neutral-200 tra-text-web-body-s hover:[&>a]:tw-text-brand-200',
                  )}
                >
                  {disclaimer}
                </Markdown>
              )}
            </div>
          )}

          <SocialLinks
            twitter="TraegerGrills"
            facebook="TraegerPelletGrills"
            instagram="traegergrills"
            pinterest="traegergrills"
            youtubeUser="TraegerGrills"
            darkMode
            width="30px"
          />
        </div>
      </div>

      <div className="tw-bg-neutral-800 tw-text-white">
        <div className="tw-flex tw-gap-y-6 tw-flex-col tw-max-w-smallBody tw-px-7 tw-py-6 tw-mx-auto tw-justify-between tw-items-start sm:tw-flex-row sm:tw-items-center">
          <div className="tw-border-b tw-border-warm-400 tw-self-stretch tw-pb-3 sm:tw-pb-0 sm:tw-border-0 tw-flex">
            <LanguageSelector showLabel />
          </div>

          <div className="tra-text-web-body-s tw-text-white">
            <span>©&nbsp;{bottomRowTitle}</span>
          </div>

          <div className="tw-flex tw-flex-col">
            <button
              onClick={() => window?.OneTrust?.ToggleInfoDisplay()}
              className={classNames(
                'tra-text-web-body-s tw-text-neutral-200 tw-block tw-py-1 tw-text-left tw-outline-offset-2',
                'focus:tw-outline-brand-200 focus:tw-outline-2 focus-visible:tw-outline',
                'hover:tw-underline hover:tw-text-brand-200',
              )}
            >
              {t('cookieConsentManager.buttonText', undefined, {
                default: 'Cookie Policy and Preferences',
              })}
            </button>

            {doNotSellText !== 'none' && (
              <Anchor
                className={classNames(
                  'tra-text-web-body-s tw-text-neutral-200 tw-block tw-py-1 tw-outline-offset-2',
                  'focus:tw-outline-brand-200 focus:tw-outline-2 focus-visible:tw-outline',
                  'hover:tw-underline hover:tw-text-brand-200',
                )}
                externalLink="https://preferences.traeger.com/dont_sell"
                isExternal
              >
                {doNotSellText}
              </Anchor>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
